import { useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Loader2, Mail, ArrowLeft } from 'lucide-react';

/**
 * MagicLinkFallback — inline card offered inside PhoneAuthPanel after the
 * user hits "Invalid phone number" 3+ times. Sends a one-tap login link
 * via email. Backend: POST /api/auth/magic-link/request  {email}.
 *
 * Always shows the same "check your inbox" toast (no enumeration signal).
 */
export default function MagicLinkFallback({ onCancel }) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!email.trim() || !email.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setSending(true);
    try {
      await api.post('/auth/magic-link/request', { email: email.trim() });
      setSent(true);
    } catch (e) {
      // Backend always returns 200 to avoid enumeration; a failure here
      // is likely network-level.
      toast.error('Could not send the link. Try again in a moment.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="magic-link-fallback"
      className="rounded-xl border border-[#EF4444]/15 bg-[#FEF2F2] px-4 py-4 space-y-3"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-[#B91C1C]/10 flex items-center justify-center flex-shrink-0">
          <Mail size={14} className="text-[#B91C1C]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-[#0F0F10] leading-tight">
            Having trouble with SMS?
          </p>
          <p className="text-[11.5px] text-[#4B4B52] leading-snug mt-0.5">
            We can send a one-tap sign-in link to your email instead. Works for landlines and VoIP numbers too.
          </p>
        </div>
      </div>

      {sent ? (
        <div className="pt-1 pl-11" data-testid="magic-link-sent">
          <p className="text-[12.5px] text-[#0F0F10] font-medium">Check your inbox.</p>
          <p className="text-[11px] text-[#4B4B52] mt-0.5">
            If that address is on file, we&apos;ve just sent a link that expires in 15 minutes.
          </p>
          <button
            type="button"
            onClick={onCancel}
            data-testid="magic-link-back-btn"
            className="text-[11.5px] font-semibold text-[#4B4B52] hover:text-[#B91C1C] transition-colors inline-flex items-center gap-1 mt-2"
          >
            <ArrowLeft size={11} /> Back to phone sign-in
          </button>
        </div>
      ) : (
        <>
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            data-testid="magic-link-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            className="rounded-lg bg-white border-black/[0.08] text-[13px] py-2.5"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              data-testid="magic-link-cancel-btn"
              className="flex-1 py-2.5 rounded-lg border border-black/[0.08] text-[12px] font-semibold hover:bg-black/[0.03] text-[#4B4B52]"
            >
              Back
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={sending}
              data-testid="magic-link-send-btn"
              className="flex-1 py-2.5 rounded-lg brand-gradient text-white text-[12px] font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Mail size={12} /> Send link</>}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
